import React, { useState, useCallback, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowUpRight, Plus, Zap, Sun, Activity, FileCheck, ClipboardList, PenTool, ShoppingCart, HardHat, FlagTriangleRight, ChevronDown } from 'lucide-react';
import { SOLUTIONS } from '../data/content';
import SolarSurveyModal from '../components/SolarSurveyModal';
import EnergyAuditModal from '../components/EnergyAuditModal';
import IndiaMap from '../components/IndiaMap';

const TES_ADVANTAGES = [
  { title: 'Trilokchand Group Legacy', text: 'From the Trilokchand Group — a trusted name in the electrical industry since 100+ years.' },
  { title: '4 NCR Manufacturing Units', text: '4 factories in the Indian National Capital Region for manufacturing high quality Low Voltage & Medium Voltage Air Insulated Switchboards up to 33kV.' },
  { title: 'Global OEM Partnerships', text: 'Product partnerships with global organisations like Schneider Electric and ABB.' },
  { title: 'Ready-Stock Warehousing', text: 'Have a warehouse for storing fast moving goods which can reduce delivery lead times.' },
  { title: 'A-Class ACE Contractor', text: 'An ACE — Affordable, Clean and Efficient — A-CLASS Certified Electrical Contractor with decades of collective experience in providing electrical engineering, procurement and construction solutions.' },
  { title: 'ISO Process Driven', text: 'A process driven ISO 9001, 14001, 45001 certified organisation following global best quality construction practices and operating procedures with the latest tools and machinery.' },
  { title: 'Ethical, Disciplined Team', text: 'One ethically driven, disciplined team of well qualified, capable and total quality-oriented engineers — working tirelessly to anticipate, meet and exceed customer expectations.' },
  { title: 'Critical Installation Preferred', text: 'First choice for critical installations — Underground Metros, Ports, Railways, Hospitals, Airports, Software IT Parks, Data Centers. Global footprint in 20+ countries over 60 years.' },
  { title: '10+ Service Centres', text: '10+ Dedicated Service centres Across India for on-ground responsiveness and after-sales support.' },
  { title: 'WIN-WIN Solution Driven', text: 'A solutions driven organisation always working to find WIN-WIN outcomes for every stakeholder.' },
  { title: 'Foundation Giving Back', text: 'We believe in giving back through our foundation — actively working in quality education, skill development, health and nutrition, and sustainable environment cum waste management.' },
];

const SOL_ICONS = [Activity, Sun, Zap];

const STAGES = [
  {
    num: '01', name: 'Initiate', icon: FlagTriangleRight,
    desc: 'We formalise the engagement, stand up the project team, and flag early risks before a single cable is drawn.',
    items: [
      { title: 'Client Contracts & Work Orders', text: 'Signed scope agreements, commercial terms and formal WOs that define every deliverable.' },
      { title: 'HOD Project Creation', text: 'Project registered in our central system with unique ID, milestones and department ownership.' },
      { title: 'Team Alignment', text: 'Core team assigned — project manager, site lead, design engineer and procurement head briefed.' },
      { title: 'Resourcing', text: 'Manpower, equipment and material requirements baselined against the project schedule.' },
      { title: 'Risks Identified', text: 'Initial risk assessment covering technical, commercial and site-specific exposures.' },
      { title: 'Cost Report (Weekly)', text: 'Recurring cost-tracking cadence established from week one to flag deviations early.' },
      { title: 'Risk Register (Weekly)', text: 'Living document updated every week — risks scored, mitigations tracked, owners assigned.' },
    ],
  },
  {
    num: '02', name: 'Plan', icon: ClipboardList,
    desc: 'Detailed scoping, scheduling, budgeting and safety planning — so every stakeholder is aligned from day one.',
    items: [
      { title: 'Scope Definition', text: 'Work breakdown structure finalised with inclusions, exclusions and interface boundaries.' },
      { title: 'General Contractors', text: 'GC coordination plan — shared timelines, access schedules and dependency mapping.' },
      { title: 'Labour & Material Planning', text: 'Resource loading chart and material procurement schedule tied to construction milestones.' },
      { title: 'Cashflow Report', text: 'Monthly cashflow projection aligned with billing milestones and vendor payment cycles.' },
      { title: 'Health & Safety Plan', text: 'Site-specific HSE plan covering PPE, toolbox talks, permits-to-work and emergency procedures.' },
      { title: 'DPR, WPR, Agenda & MOMs', text: 'Daily and weekly progress reports, meeting agendas and minutes — documented accountability.' },
      { title: 'Change Management', text: 'Formal change-order process for scope variations — impact assessed before any deviation.' },
    ],
  },
  {
    num: '03', name: 'Design', icon: PenTool,
    desc: 'From concept to tender-ready drawings — every schematic reviewed, every BOQ validated, every exclusion documented.',
    items: [
      { title: 'Concept Design', text: 'High-level single-line diagrams, layout options and technology selection for client review.' },
      { title: 'Schematic Design', text: 'Detailed circuit schematics, control logic and protection coordination studies.' },
      { title: 'Detail Design', text: 'GA drawings, cable schedules, earthing layouts and panel fabrication drawings for execution.' },
      { title: 'Tender Drawings', text: 'Issue-for-tender drawing set packaged with specifications for vendor bidding.' },
      { title: 'Tender BOQs', text: 'Bill of quantities with item descriptions, units, rates and allowances — ready for pricing.' },
      { title: 'Exclusions', text: 'Clear documentation of what is outside scope — preventing disputes downstream.' },
      { title: 'Design Audit', text: 'Independent review of all design deliverables before release — first-time-right approach.' },
    ],
  },
  {
    num: '04', name: 'Procure', icon: ShoppingCart,
    desc: 'Strategic vendor selection, OEM partnerships and contract issuance — quality materials, competitive pricing.',
    items: [
      { title: 'RFP & Appoint Sub-Vendors', text: 'Formal request-for-proposal issued, evaluated and awarded to qualified subcontractors.' },
      { title: 'OEM Partnerships', text: 'Direct sourcing from original equipment manufacturers for critical switchgear and transformers.' },
      { title: 'Empanelled Sub-Vendor Pool', text: 'Pre-qualified vendor registry maintained by category — rated on quality, delivery and safety.' },
      { title: 'Release LOIs, Contracts & WOs', text: 'Letters of intent, contracts and purchase orders issued with clear delivery and quality terms.' },
    ],
  },
  {
    num: '05', name: 'Construct', icon: HardHat,
    desc: 'Boots on the ground — mobilisation, quality control, weekly reviews and a final audit before handover.',
    items: [
      { title: 'Kick-off Meeting', text: 'Formal start with all stakeholders — scope walkthrough, safety induction and schedule confirmation.' },
      { title: 'Site Mobilization', text: 'Equipment, materials and crew deployed — site office, stores and safety infrastructure set up.' },
      { title: 'Quality Assurance', text: 'Inspection and test plans executed at every stage — torque checks, megger tests, IR scans.' },
      { title: 'Weekly Progress Reviews', text: 'Site walk, photo documentation, S-curve tracking and deviation analysis every week.' },
      { title: 'Final Audit', text: 'Pre-commissioning checks, snag list clearance and readiness sign-off before energisation.' },
    ],
  },
  {
    num: '06', name: 'Close', icon: FileCheck,
    desc: 'Warranties documented, dashboards reviewed, lessons learnt captured — every project closed with accountability.',
    items: [
      { title: 'Warranties', text: 'All equipment and workmanship warranties compiled, registered and handed to the client.' },
      { title: 'OneDrive Project Folder', text: 'Complete project archive — drawings, test reports, certificates and correspondence preserved.' },
      { title: 'Weekly Dashboard Reviews', text: 'Final performance metrics reviewed — cost vs. budget, schedule vs. plan, quality KPIs.' },
      { title: 'Commercial Closure', text: 'Final billing, retention release schedule and vendor account settlement completed.' },
      { title: 'Completion Audit', text: 'Independent audit confirming all deliverables met — quality, safety and commercial sign-off.' },
      { title: 'Lessons Learnt', text: 'Structured debrief documenting what worked, what didn\'t and improvements for the next project.' },
    ],
  },
];

const PRODUCTS = [
  { letter: 'A', name: 'High Side Works', tag: 'CGI', items: [
    { sno: 1, item: 'Power Transformers' },
    { sno: 2, item: 'CSSA / PSSA — Compact / Packaged Substation Assemblies up to 33 KV' },
    { sno: 3, item: 'Distribution Transformers (250 KVA to 3000 KVA)' },
    { sno: 4, item: 'Ring Main Units (RMUs)' },
    { sno: 5, item: 'MV Panels up to 33 KV' },
    { sno: 6, item: 'LV Panels up to 415 V' },
    { sno: 7, item: 'HT Capacitor Panels' },
    { sno: 8, item: 'LT APFCR Panels' },
    { sno: 9, item: 'PDU — Power Distribution Units' },
    { sno: 10, item: 'Diesel Generator (DG) Sets' },
    { sno: 11, item: 'PNG Gas Generator Sets' },
    { sno: 12, item: 'UPS — Uninterrupted Power Supply' },
    { sno: 13, item: 'Servo Stabilizer' },
    { sno: 14, item: 'Control & Relay Panel' },
  ]},
  { letter: 'B', name: 'Switchyards', tag: 'T&D', items: [
    { sno: 15, item: 'ACSR Conductor' },
    { sno: 16, item: 'CT / PT — Current / Potential Transformer' },
    { sno: 17, item: 'Lightning Arrestor' },
    { sno: 18, item: 'Isolator with / without Earth Switch' },
    { sno: 19, item: 'Outdoor Switchgear' },
    { sno: 20, item: 'Up to 33,000 V Cables' },
    { sno: 21, item: 'Up to 1,100 V Cables' },
    { sno: 22, item: 'HT / LT Termination Kit — Indoor / Outdoor' },
    { sno: 23, item: 'Cable Lugs' },
    { sno: 24, item: 'Cable Glands' },
  ]},
  { letter: 'C', name: 'Low Side Works', tag: 'LSW', items: [
    { sno: 25, item: 'Earthing — Chemical' },
    { sno: 26, item: 'Industrial Socket' },
    { sno: 27, item: 'Conduit — GI / MS / PVC' },
    { sno: 28, item: 'Wires' },
    { sno: 29, item: 'Switch Socket Plate & Box' },
    { sno: 30, item: 'Lighting Fixtures' },
    { sno: 31, item: 'Lighting / Track Busways System' },
    { sno: 32, item: 'Compact Busduct (400 A – 800 A)' },
    { sno: 33, item: 'Sandwich Busducts (1000 – 6300 A)' },
    { sno: 34, item: 'Rising Mains' },
    { sno: 35, item: 'Meter Boards' },
    { sno: 36, item: 'Cable Tray — Ladder / Perforated' },
    { sno: 37, item: 'Cable Tray with Cover / Raceways' },
    { sno: 38, item: 'Cable Tray — Wire Mesh' },
    { sno: 39, item: 'ATS with Enclosure' },
    { sno: 40, item: 'Final Distribution Board (FDB)' },
  ]},
  { letter: 'D', name: 'Extra Low Voltage Works', tag: 'ELV', items: [
    { sno: 41, item: 'CCTV / LCD / HMI' },
    { sno: 42, item: 'PA System' },
    { sno: 43, item: 'PIR Sensors' },
    { sno: 44, item: 'Humidity & Temperature Sensors' },
    { sno: 45, item: 'KWH Energy Meter' },
    { sno: 46, item: 'PLCs' },
  ]},
  { letter: 'E', name: 'MEP Works', tag: 'MEP', items: [
    { sno: 47, item: 'HVLS Fans' },
    { sno: 48, item: 'Fire Alarm Systems' },
    { sno: 49, item: 'Plumbing — Pipes — RCC / DWC / HDPE' },
  ]},
  { letter: 'F', name: 'Clean Energy', tag: 'Solar', items: [
    { sno: 50, item: 'Photovoltaic (PV) Modules' },
    { sno: 51, item: 'Inverter' },
    { sno: 52, item: 'Mounting Structure' },
    { sno: 53, item: 'DC Cables' },
    { sno: 54, item: 'Lightning Protection System' },
    { sno: 55, item: 'Electric Vehicle Chargers' },
  ]},
];

function ProductCategory({ cat, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="reveal bg-white border border-[var(--line)] rounded-2xl overflow-hidden hover:border-[var(--orange)]/40 transition-colors" style={{ transitionDelay: `${index * 50}ms` }} data-testid={`product-cat-${cat.letter}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-4 p-6 md:p-7 text-left">
        <span className="font-display font-bold text-[22px] md:text-[26px] text-[var(--orange)] w-10 shrink-0">{cat.letter}</span>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-[18px] md:text-[20px] leading-tight">{cat.name}</div>
          <div className="text-[12px] text-[var(--text-light)] mt-1">{cat.items.length} products</div>
        </div>
        <span className="px-3 py-1 rounded-full bg-[var(--orange)]/10 text-[var(--orange)] text-[11px] font-semibold uppercase tracking-wider shrink-0">{cat.tag}</span>
        <ChevronDown className={`w-5 h-5 text-[var(--text-mid)] transition-transform duration-300 shrink-0 ${open ? 'rotate-180' : ''}`}/>
      </button>
      {open && (
        <div className="px-6 md:px-7 pb-6 md:pb-7 border-t border-[var(--line)]">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-0 pt-4">
            {cat.items.map((p) => (
              <div key={p.sno} className="flex items-baseline gap-3 py-2.5 border-b border-[var(--line)]/50 last:border-b-0">
                <span className="font-display font-bold text-[11px] text-[var(--orange)] shrink-0 w-6 text-right">{p.sno}</span>
                <span className="text-[14px] text-[var(--ink)] leading-snug">{p.item}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Services() {
  const [activeStage, setActiveStage] = useState(0);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const onHeroLoad = useCallback(() => setHeroLoaded(true), []);
  const [solarOpen, setSolarOpen] = useState(false);
  const [energyOpen, setEnergyOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open survey modal when arriving via ?survey=solar | ?survey=energy
  useEffect(() => {
    const s = searchParams.get('survey');
    if (s === 'solar') {
      setSolarOpen(true);
      searchParams.delete('survey');
      setSearchParams(searchParams, { replace: true });
    } else if (s === 'energy') {
      setEnergyOpen(true);
      searchParams.delete('survey');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <main>
      {/* Hero — inverted (text right, image left) with About-style gradient */}
      <section className="relative overflow-hidden" style={{ height: '120svh', minHeight: '700px' }} data-testid="services-hero" data-nav-theme="dark">
        <div className="absolute inset-0">
          <div className={`absolute inset-0 bg-[var(--ink)] transition-opacity duration-700 ${heroLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}><div className="absolute inset-0 shimmer-bg"/></div>
          <img src="/img/wwd-hero.jpg" alt="" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`} loading="eager" fetchPriority="high" onLoad={onHeroLoad}/>
          <div className="absolute inset-0 bg-gradient-to-l from-[var(--ink)]/80 from-15% via-[var(--ink)]/60 via-50% to-[var(--ink)]/25"/>
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/50 via-transparent to-[var(--ink)]/15"/>
        </div>
        <div className="absolute inset-0 noise pointer-events-none opacity-20"/>
        <div className="relative h-full px-6 md:px-10 lg:px-14 max-w-[1500px] mx-auto flex items-center justify-end">
          <div className="max-w-3xl text-right">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[var(--orange)] font-semibold mb-5">What We Do</div>
            <h1 className="font-display text-[clamp(40px,6vw,86px)] leading-[1.02] font-bold tracking-tight" style={{color:'#fff'}}>
              Electrical & Solar EPC,<br/>
              <span className="font-serif-it text-[var(--orange)]">engineered end-to-end.</span>
            </h1>
            <p className="mt-7 text-[18px] md:text-[19px] text-white max-w-2xl leading-relaxed font-medium ml-auto" data-testid="services-hero-desc">From pre-order engineering to commissioning and AMC — a process-driven approach across electrical, solar and energy-efficiency disciplines.</p>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-60">
          <div className="w-px h-12 bg-white/50 animate-pulse"/>
          <span className="text-[10px] uppercase tracking-[0.2em] text-white/70">Scroll</span>
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 bg-[var(--cream)]">
        <div className="max-w-[1500px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5 reveal">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-4">Our Solutions</div>
              <h2 className="font-display text-[clamp(30px,3.8vw,56px)] leading-[1.05] font-bold tracking-tight">
                Affordable, Clean & Efficient<br/><span className="font-serif-it text-[var(--orange)]">A-Class EPC.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 reveal">
              <p className="text-[17px] text-[var(--text-mid)] leading-[1.85]">Three integrated practice areas — each delivered with in-house engineering, full procurement control, and TQM-driven execution. Whether it&apos;s an EHV substation, a rooftop PV plant or a facility-wide IoT energy monitoring system, our teams work as one to deliver the right outcome.</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-5">
            {SOLUTIONS.map((s,i) => {
              const Ic = SOL_ICONS[i];
              const sectionIds = ['transmission', 'solar', 'energy'];
              return (
                <article key={i} id={sectionIds[i]} className="group bg-white rounded-3xl border border-[var(--line)] p-8 md:p-10 hover-lift hover:border-[var(--orange)]/40 reveal scroll-mt-24 flex flex-col" style={{ transitionDelay: `${i*80}ms` }}>
                  <div className="flex items-center justify-between mb-8">
                    <div className="w-16 h-16 rounded-2xl bg-[var(--orange)]/10 flex items-center justify-center group-hover:bg-[var(--orange)] transition-colors">
                      <Ic className="w-8 h-8 text-[var(--orange)] group-hover:text-white transition-colors" strokeWidth={2}/>
                    </div>
                    <span className="font-display font-bold text-[14px] text-[var(--text-light)]">{String(i+1).padStart(2,'0')}</span>
                  </div>
                  <h3 className="font-display font-bold text-[26px] leading-tight mb-6">{s.title}</h3>
                  <ul className="space-y-3 flex-1">
                    {s.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-3 text-[15px] text-[var(--text-mid)] py-2 border-t border-[var(--line)] first:border-t-0">
                        <Plus className="w-4 h-4 text-[var(--orange)] mt-1 shrink-0"/>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                  {i === 1 && (
                    <button onClick={() => setSolarOpen(true)} className="mt-8 w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-[var(--orange)]/10 border border-[var(--orange)]/30 text-[var(--orange)] font-semibold text-[14px] hover:bg-[var(--orange)] hover:text-white transition-all" data-testid="request-survey-btn">
                      <Sun className="w-4 h-4"/> Request Solar Survey
                    </button>
                  )}
                  {i === 2 && (
                    <button onClick={() => setEnergyOpen(true)} className="mt-8 w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-[var(--blue)]/10 border border-[var(--blue)]/30 text-[var(--blue)] font-semibold text-[14px] hover:bg-[var(--blue)] hover:text-white transition-all" data-testid="request-audit-btn">
                      <Activity className="w-4 h-4"/> Request Energy Audit
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Products — Proposed Make List */}
      <section id="products" className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 bg-[var(--cream-deep)]" data-testid="products-section">
        <div className="max-w-[1500px] mx-auto">
          <div className="reveal mb-14 max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-4">Our Products</div>
            <h2 className="font-display text-[clamp(30px,3.8vw,56px)] leading-[1.05] font-bold tracking-tight">
              Total quality <span className="font-serif-it text-[var(--orange)]">products & services.</span>
            </h2>
            <p className="mt-5 text-[16px] text-[var(--text-mid)] leading-[1.85]">Global-quality products sourced from trusted OEMs across six categories — from high-side power equipment to clean energy systems.</p>
          </div>

          <div className="space-y-4">
            {PRODUCTS.map((cat, ci) => (
              <ProductCategory key={ci} cat={cat} index={ci} />
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Process — 6 interactive stages */}
      <section id="process" className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 bg-[var(--ink)] text-white relative overflow-hidden" data-nav-theme="dark" data-testid="delivery-process">
        <div className="absolute inset-0 grid-bg-dark opacity-40"/>
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[var(--orange)]/10 blur-3xl"/>
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[var(--blue)]/8 blur-3xl"/>
        <div className="relative max-w-[1500px] mx-auto">
          <div className="reveal mb-16 max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-4">How We Work</div>
            <h2 className="font-display text-[clamp(30px,3.8vw,56px)] leading-[1.05] font-bold tracking-tight">
              Project delivery <span className="font-serif-it text-[var(--orange)]">process.</span>
            </h2>
            <p className="mt-5 text-[16px] text-white/65 leading-[1.85] max-w-2xl">Six disciplined stages ensure every Tricolite project is delivered on time, within budget, and to the highest quality and safety standards.</p>
          </div>

          {/* Stage navigation pills */}
          <div className="flex flex-wrap gap-2 mb-10 reveal" data-testid="stage-nav">
            {STAGES.map((s, i) => (
              <button
                key={i}
                onClick={() => setActiveStage(i)}
                onMouseEnter={() => setActiveStage(i)}
                className={`inline-flex items-center gap-2.5 px-5 py-3 rounded-full text-[13px] font-semibold uppercase tracking-wider transition-all duration-400 ${
                  activeStage === i
                    ? 'bg-[var(--orange)] text-white shadow-[0_8px_24px_-8px_rgba(243,118,58,0.5)]'
                    : 'bg-white/[0.06] border border-white/10 text-white/60 hover:text-white hover:border-white/25'
                }`}
                data-testid={`stage-btn-${i+1}`}
              >
                <span className="font-display">{s.num}</span>
                <span>{s.name}</span>
              </button>
            ))}
          </div>

          {/* Active stage detail */}
          <div className="grid lg:grid-cols-12 gap-6 reveal" data-testid="stage-detail">
            {/* Left: stage info */}
            <div className="lg:col-span-5">
              <div className="bg-white/[0.04] border border-white/10 rounded-3xl p-8 md:p-10 h-full">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-[var(--orange)]/15 flex items-center justify-center">
                    {React.createElement(STAGES[activeStage].icon, { className: 'w-7 h-7 text-[var(--orange)]', strokeWidth: 2 })}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--blue)] font-semibold">Stage {STAGES[activeStage].num}</div>
                    <div className="font-display font-bold text-[28px] md:text-[32px] leading-none mt-1" style={{color:'#fff'}}>{STAGES[activeStage].name}</div>
                  </div>
                </div>
                <p className="text-[15px] md:text-[16px] text-white/75 leading-[1.85]">{STAGES[activeStage].desc}</p>

                {/* Progress indicator */}
                <div className="mt-8 flex items-center gap-2">
                  {STAGES.map((_, i) => (
                    <div
                      key={i}
                      className={`h-1 rounded-full transition-all duration-500 ${
                        i <= activeStage ? 'bg-[var(--orange)] flex-[2]' : 'bg-white/15 flex-1'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Right: deliverables */}
            <div className="lg:col-span-7">
              <div className="grid sm:grid-cols-2 gap-3">
                {STAGES[activeStage].items.map((item, j) => (
                  <div
                    key={`${activeStage}-${j}`}
                    className="group/item flex items-start gap-4 p-5 rounded-2xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-[var(--orange)]/30 transition-all duration-300"
                    style={{ animationDelay: `${j * 60}ms` }}
                    data-testid={`stage-item-${activeStage}-${j}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-[var(--orange)]/12 flex items-center justify-center shrink-0 mt-0.5 group-hover/item:bg-[var(--orange)]/25 transition-colors">
                      <span className="font-display font-bold text-[11px] text-[var(--orange)]">{String(j+1).padStart(2,'0')}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[14px] md:text-[15px] text-white/90 font-semibold leading-snug">{item.title}</div>
                      <div className="text-[12px] md:text-[13px] text-white/50 leading-[1.6] mt-1.5">{item.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile accordion fallback */}
          <div className="mt-10 lg:hidden space-y-3">
            {STAGES.map((s, i) => (
              <details key={i} className="group/acc rounded-2xl bg-white/[0.04] border border-white/10 overflow-hidden" data-testid={`stage-accordion-${i+1}`}>
                <summary className="flex items-center gap-4 p-5 cursor-pointer list-none">
                  <span className="font-display font-bold text-[var(--orange)] text-[18px]">{s.num}</span>
                  <span className="font-display font-bold text-[18px] flex-1" style={{color:'#fff'}}>{s.name}</span>
                  <ChevronDown className="w-5 h-5 text-white/50 group-open/acc:rotate-180 transition-transform"/>
                </summary>
                <div className="px-5 pb-5 space-y-2">
                  <p className="text-[14px] text-white/65 leading-[1.8] mb-3">{s.desc}</p>
                  {s.items.map((item, j) => (
                    <div key={j} className="flex items-start gap-3 py-2.5 border-t border-white/5">
                      <span className="font-display font-bold text-[10px] text-[var(--orange)] mt-0.5">{String(j+1).padStart(2,'0')}</span>
                      <div>
                        <span className="text-[14px] text-white/85 font-medium">{item.title}</span>
                        <p className="text-[12px] text-white/45 leading-[1.5] mt-0.5">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Focus areas grid removed per request */}

      {/* Tricolite EPC Advantage — 11 differentiators + India map */}
      <section id="epc-advantage" className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 bg-[var(--cream)] relative overflow-hidden" data-testid="epc-advantage-section">
        <div className="absolute -top-32 -right-40 w-[560px] h-[560px] rounded-full bg-[var(--orange)]/6 blur-3xl pointer-events-none"/>
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[var(--blue)]/8 blur-3xl pointer-events-none"/>

        <div className="relative max-w-[1500px] mx-auto">
          {/* Section heading */}
          <div className="grid lg:grid-cols-12 gap-10 mb-14 lg:mb-16">
            <div className="lg:col-span-5 reveal">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-4">Why Tricolite Energy Solutions</div>
              <h2 className="font-display text-[clamp(30px,3.8vw,56px)] leading-[1.05] font-bold tracking-tight">
                Tricolite EPC <br/><span className="font-serif-it text-[var(--orange)]">Advantage.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 reveal">
              <p className="text-[17px] text-[var(--text-mid)] leading-[1.85]">
                A century-old engineering DNA, backed by four NCR factories, global OEM partnerships and an ISO-certified process — with a nationwide service network and a solutions-first mindset that keeps every project moving forward.
              </p>
            </div>
          </div>

          {/* Content grid: advantages (left) + India map (right) */}
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Left: 11 advantages */}
            <div className="lg:col-span-7">
              <ol className="grid sm:grid-cols-2 gap-3">
                {TES_ADVANTAGES.map((a, i) => (
                  <li
                    key={i}
                    className="reveal group/adv flex items-start gap-4 p-5 rounded-2xl bg-white border border-[var(--line)] hover:border-[var(--orange)]/40 hover:shadow-[0_10px_28px_-16px_rgba(14,22,32,0.18)] transition-all duration-400"
                    style={{ transitionDelay: `${i * 40}ms` }}
                    data-testid={`epc-adv-${i+1}`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-[var(--orange)]/10 flex items-center justify-center shrink-0 mt-0.5 group-hover/adv:bg-[var(--orange)] transition-colors duration-300">
                      <span className="font-display font-bold text-[12px] text-[var(--orange)] group-hover/adv:text-white transition-colors duration-300">{String(i+1).padStart(2,'0')}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-bold text-[15px] md:text-[16px] leading-snug text-[var(--ink)]">{a.title}</div>
                      <p className="text-[13px] md:text-[13.5px] text-[var(--text-mid)] leading-[1.7] mt-1.5">{a.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Right: India map illustration */}
            <div className="lg:col-span-5 lg:sticky lg:top-24">
              <div className="reveal">
                <IndiaMap className="w-full aspect-[600/640] max-w-[520px] mx-auto" />

                {/* Quick summary chips below the map */}
                <div className="mt-8 grid grid-cols-3 gap-3">
                  {[
                    { k: '100+', v: 'Years Group Legacy' },
                    { k: '4', v: 'NCR Factories' },
                    { k: '10+', v: 'Service Centres' },
                  ].map((s, i) => (
                    <div key={i} className="rounded-2xl bg-white border border-[var(--line)] px-4 py-4 text-center hover:border-[var(--orange)]/40 transition-colors">
                      <div className="font-display font-bold text-[22px] md:text-[24px] text-[var(--ink)] leading-none">{s.k}</div>
                      <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--text-light)] mt-2 leading-tight">{s.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA — dark continuation from process section, orange gradient panel for punch */}
      <section className="px-6 md:px-10 lg:px-14 pb-20 bg-[var(--ink)] relative overflow-hidden" data-nav-theme="dark">
        <div className="absolute inset-0 grid-bg-dark opacity-30 pointer-events-none"/>
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[var(--orange)]/12 blur-3xl pointer-events-none"/>
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[var(--blue)]/8 blur-3xl pointer-events-none"/>
        <div className="max-w-[1500px] mx-auto relative overflow-hidden rounded-3xl p-10 md:p-16 border border-[var(--orange)]/30" style={{background:'linear-gradient(135deg, #F3763A 0%, #E85A1A 55%, #C04617 100%)'}}>
          <div className="absolute inset-0 opacity-20" style={{backgroundImage:'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.35) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.25) 0%, transparent 50%)'}}/>
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-8 text-white">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-white/80 font-semibold mb-3">Let&apos;s Build Something</div>
              <h2 className="font-display text-[clamp(28px,3.4vw,46px)] leading-[1.05] font-bold max-w-3xl">Tell us about your <span className="font-serif-it">project.</span></h2>
            </div>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-[var(--ink)] hover:bg-[var(--ink)] hover:text-white border border-white px-7 py-4 rounded-full font-semibold transition-colors shrink-0" data-testid="services-cta">Request a Proposal <ArrowUpRight className="w-4 h-4"/></Link>
          </div>
        </div>
      </section>
      <SolarSurveyModal open={solarOpen} onClose={() => setSolarOpen(false)}/>
      <EnergyAuditModal open={energyOpen} onClose={() => setEnergyOpen(false)}/>
    </main>
  );
}
